/* eslint-disable react/forbid-prop-types */
import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import {
  Route,
  BrowserRouter,
  Routes as RouterRoutes,
  Navigate,
} from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

const CookiesAccept = lazy(() => import('./components/CookiesAccept'));
const ScrollToTop = lazy(() => import('./shared/ScrollToTop'));

const Homepage = lazy(() => import('./components/homepage/HomePage'));
const Register = lazy(() => import('./components/register/RegisterPage'));
const Login = lazy(() => import('./components/login/LoginPage'));
const Logout = lazy(() => import('./components/logout/LogoutPage'));
const Account = lazy(() => import('./components/account/AccountPage'));
const Profile = lazy(() => import('./components/indv_pages/ProfilePage'));
const ClassFinder = lazy(() => import('./components/finder/class/ClassFinder'));
const TrainingFinder = lazy(() => import('./components/finder/training/TrainingFinder'));
const TeacherFinder = lazy(() => import('./components/finder/teacher/Finder'));
const TrainerFinder = lazy(() => import('./components/finder/trainer/Finder'));
const Class = lazy(() => import('./components/indv_pages/ClassPage'));
const Location = lazy(() => import('./components/indv_pages/LocationPage'));
const PrivacyPage = lazy(() => import('./components/indv_pages/PrivacyPage'));
const TermsPage = lazy(() => import('./components/indv_pages/TermsPage'));
const CCPAPage = lazy(() => import('./components/indv_pages/CCPAPage'));
const ContactPage = lazy(() => import('./components/indv_pages/ContactPage'));
const AboutMembershipPage = lazy(() => import('./components/indv_pages/AboutMembershipPage'));
const SpecialtyTrainings = lazy(() => import('./components/trainings/specialtytrainings/SpecialtyTrainingsPage'));
const BodyLifeTrainings = lazy(() => import('./components/trainings/bodylifetrainings/BodyLifeTrainingsPage'));
const Shop = lazy(() => import('./components/shop/ShopPage'));
const Activate = lazy(() => import('./components/indv_pages/ActivatePage'));
const RecoverAccount = lazy(() => import('./components/account_recovery/RecoverAccountPage'));
const ResetPassword = lazy(() => import('./components/account_recovery/ResetPasswordPage'));
const NotFoundPage = lazy(() => import('./components/indv_pages/NotFoundPage'));
const Join = lazy(() => import('./components/indv_pages/JoinPage'));
const AboutEvents = lazy(() => import('./components/indv_pages/AboutEventsPage'));
const AboutClasses = lazy(() => import('./components/indv_pages/AboutClassesPage'));
const AboutTrainings = lazy(() => import('./components/indv_pages/AboutTrainingsPage'));
const DebbieRosasEvents = lazy(() => import('./components/events/DebbieRosasEventsPage'));
const PressRoom = lazy(() => import('./components/indv_pages/PressRoom'));
const AboutNiaPage = lazy(() => import('./components/indv_pages/AboutNiaPage'));
const ClassTypesPage = lazy(() => import('./components/classes/ClassTypesPage'));
const TeachPage = lazy(() => import('./components/indv_pages/TeachPage'));
const DEICommitmentPage = lazy(() => import('./components/indv_pages/DEICommitmentsPage'));
const SignupThankYou = lazy(() => import('./components/indv_pages/EmailSignupThankYou'));
const MasqueradePage = lazy(() => import('./components/masquerade/MasqueradePage'));
const Blog = lazy(() => import('./components/blog/Blog'));
const BlogPostDetail = lazy(() => import('./components/blog/BlogPostDetail'));
const AboutBeltsPage = lazy(() => import('./components/indv_pages/AboutBeltsPage'));
const FAQPage = lazy(() => import('./components/indv_pages/FAQPage'));
const NiaDayGuidePage = lazy(() => import('./components/leadgen_pages/NiaDayGuidePage'));
const NiaDayGuideThankYou = lazy(() => import('./components/leadgen_pages/NiaDayGuideThankYou'));
const HolisticFitnessPage = lazy(() => import('./components/leadgen_pages/HolisticFitnessPage'));
const HolisticFitnessThankYouPage = lazy(() => import('./components/leadgen_pages/HolisticFitnessThankYouPage'));
const BarefootDayPage = lazy(() => import('./components/leadgen_pages/BarefootDayPage'));
const BarefootDayThankYou = lazy(() => import('./components/leadgen_pages/BarefootDayThankYou'));
const SalePage = lazy(() => import('./components/indv_pages/SalePage'));
const TestimonialsPage = lazy(() => import('./components/indv_pages/TestimonialsPage'));
// const MaintenanceMode = lazy(() => import('./components/MaintenanceMode'));

const styles = () => ({
  root: {
    display: 'flex',
  },
  loading: {
    height: '100vh',
    width: '100vw',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
});

function Routes(props) {
  const { classes } = props;
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Suspense fallback={<div className={classes.loading}><CircularProgress color="secondary" /></div>}>
        <ScrollToTop>
          <div className={classes.root}>
            <RouterRoutes>
              <Route path="/" element={<Homepage />} />
              {/* REDIRECTS */}
              <Route path="/geofind" element={<Navigate to="/finder/class" replace />} />
              <Route path="/finder/class/inperson" element={<Navigate to="/finder/class?attendance_type=inperson" replace />} />
              <Route path="/finder/class/online" element={<Navigate to="/finder/class?attendance_type=online" replace />} />
              <Route path="/finder/training/inperson" element={<Navigate to="/finder/training?attendance_type=inperson" replace />} />
              <Route path="/finder/training/online" element={<Navigate to="/finder/training?attendance_type=online" replace />} />
              {/* USER */}
              <Route path="/register" element={<Register />} />
              <Route path="/activate/:id/:token" element={<Activate />} />
              <Route path="/account_recovery" element={<RecoverAccount />} />
              <Route path="/password_reset/:id/:token" element={<ResetPassword />} />
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              {/* ACCOUNT */}
              <Route path="/account/*" element={<Account />} />
              {/* FINDER */}
              <Route path="/finder/class/:query?" element={<ClassFinder />} />
              <Route path="/finder/training/:query?" element={<TrainingFinder />} />
              <Route path="/finder/teacher/" element={<TeacherFinder />} />
              <Route path="/finder/trainer/" element={<TrainerFinder />} />
              {/* BLOG */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:idOrSlug" element={<BlogPostDetail />} />
              {/* INDV PAGES */}
              <Route path="/about-nia" element={<AboutNiaPage />} />
              <Route path="/user/:userNameOrId" element={<Profile />} />
              <Route path="/class/:id" element={<Class />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/ccpa" element={<CCPAPage />} />
              <Route path="/membership" element={<AboutMembershipPage />} />
              <Route path="/location/:id" element={<Location />} />
              <Route path="/specialtytrainings/:trainingType?" element={<SpecialtyTrainings />} />
              <Route path="/trainings/:trainingType?" element={<BodyLifeTrainings />} />
              <Route path="/join" element={<Join />} />
              <Route path="/class-intro" element={<AboutClasses />} />
              <Route path="/classes/:classType?" element={<ClassTypesPage />} />
              <Route path="/training-intro" element={<AboutTrainings />} />
              <Route path="/event-intro" element={<AboutEvents />} />
              <Route path="/debbie-rosas-events" element={<DebbieRosasEvents />} />
              <Route path="/press-room" element={<PressRoom />} />
              <Route path="/teach" element={<TeachPage />} />
              <Route path="/dei" element={<DEICommitmentPage />} />
              <Route path="/masquerade/:username/:token" element={<MasqueradePage />} />
              <Route path="/signup_thankyou" element={<SignupThankYou />} />
              <Route path="/belt-tutorials" element={<AboutBeltsPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/sale" element={<SalePage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              {/* LEAD GEN */}
              <Route path="/nia-day-guide" element={<NiaDayGuidePage />} />
              <Route path="/nia-day-thankyou" element={<NiaDayGuideThankYou />} />
              <Route path="/holistic-fitness-report" element={<HolisticFitnessPage />} />
              <Route path="/holistic-fitness-thankyou" element={<HolisticFitnessThankYouPage />} />
              <Route path="/barefoot-day" element={<BarefootDayPage />} />
              <Route path="/barefoot-day-thankyou" element={<BarefootDayThankYou />} />
              {/* SHOP */}
              <Route path="/shop/*" element={<Shop />} />
              {/* NOT FOUND (must be last) */}
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </RouterRoutes>
          </div>
        </ScrollToTop>
        <CookiesAccept />
      </Suspense>
    </BrowserRouter>
  );
}

// uncomment for maintenance mode
// function Routes(props) {
//   const { classes } = props;
//   return (
//     <BrowserRouter>
//       <Suspense fallback={<div className={classes.loading}><CircularProgress color="secondary" /></div>}>
//         <ScrollToTop>
//           <div className={classes.root}>
//             <RouterRoutes>
//               <Route path="*" element={<MaintenanceMode />} />
//             </RouterRoutes>
//           </div>
//         </ScrollToTop>
//         <CookiesAccept />
//       </Suspense>
//     </BrowserRouter>
//   );
// }

Routes.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Routes);
